
import { useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PersonalInfoForm } from "./components/PersonalInfoForm";
import { PasswordChangeForm } from "./components/PasswordChangeForm";
import { ProfileAvatar } from "./components/ProfileAvatar";
import { ProfileHeader } from "./components/ProfileHeader";
import { AppearanceSettings } from "@/components/settings/components/AppearanceSettings";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslations } from "@/hooks/useTranslations";

export const StudentSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslations("settings");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: user?.email || "",
    country: "",
    city: ""
  });
  const [avatarUrl, setAvatarUrl] = useState("");

  // Fetch student data
  const { data: studentData } = useQuery({
    queryKey: ["student-data", user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      
      const { data, error } = await supabase
        .from("students")
        .select("id, first_name, last_name, phone, email, avatar_url, country, city")
        .eq("email", user.email)
        .single();
        
      if (error) {
        console.error("Error fetching student data:", error);
        return null;
      }
      
      if (data) {
        // Update form data with fetched values
        setFormData({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          phone: data.phone || "",
          email: data.email || user.email || "",
          country: data.country || "",
          city: data.city || ""
        });
        
        setAvatarUrl(data.avatar_url || "");
      }
      
      return data;
    },
    enabled: !!user?.email
  });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!studentData?.id) return;
    
    setLoading(true);
    
    try {
      const { error } = await supabase
        .from("students")
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          country: formData.country,
          city: formData.city
        })
        .eq("id", studentData.id);
        
      if (error) throw error;
      
      toast.success("Informações atualizadas com sucesso");
    } catch (error) {
      console.error("Error updating student info:", error);
      toast.error("Erro ao atualizar informações");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpdate = (url: string) => {
    setAvatarUrl(url);
  };

  return (
    <div className="space-y-6">
      <ProfileHeader />
      
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 dark:border-gray-700/50 p-6 hover:shadow-lg transition-all overflow-hidden">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <ProfileAvatar 
              studentId={studentData?.id || ""}
              firstName={formData.first_name}
              lastName={formData.last_name}
              avatarUrl={avatarUrl}
              onAvatarUpdate={handleAvatarUpdate}
            />
            
            <div className="flex-1 w-full">
              <Tabs defaultValue="personal" className="w-full">
                <ScrollArea className="w-full pb-3">
                  <TabsList className="mb-4 bg-slate-50/80 dark:bg-gray-900/80 rounded-xl w-max min-w-full">
                    <TabsTrigger value="personal">{t("tabs.profile")}</TabsTrigger>
                    <TabsTrigger value="security">{t("tabs.security")}</TabsTrigger>
                    <TabsTrigger value="appearance">{t("tabs.appearance")}</TabsTrigger>
                  </TabsList>
                </ScrollArea>
                
                <TabsContent value="personal" className="rounded-xl">
                  <PersonalInfoForm 
                    loading={loading}
                    formData={formData}
                    onChange={handleFormChange}
                    onSubmit={handleFormSubmit}
                  />
                </TabsContent>
                
                <TabsContent value="security" className="rounded-xl">
                  <PasswordChangeForm />
                </TabsContent>
                
                <TabsContent value="appearance" className="rounded-xl">
                  <AppearanceSettings />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
